import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CartDrawer from '../home/components/CartDrawer';
import { useSEO } from '../../hooks/useSEO';

export default function ContactPage() {
  const [activeSection, setActiveSection] = useState('get-in-touch');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://motionboyz.org';

  useSEO({
    title: 'Contact Us | Motion Boyz – Get In Touch',
    description:
      'Contact Motion Boyz for questions about orders, returns, damaged items, or any inquiries. We respond within 24–48 hours.',
    canonical: '/contact',
    ogImage: `${siteUrl}/og-image.jpg`,
    ogType: 'website',
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const sectionIds = ['get-in-touch', 'quick-links', 'support-hours'];
    const handleScroll = () => {
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= 300) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const sections = [
    { id: 'get-in-touch', label: 'Get in Touch' },
    { id: 'quick-links', label: 'Quick Links' },
    { id: 'support-hours', label: 'Support Hours' },
  ];

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <CartDrawer />

      {/* Hero Section */}
      <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/58edf040d576283f95ca6ae7d9df1926.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        <div className="relative z-10 text-center px-6 max-w-4xl w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 mb-6">
            <i className="ri-customer-service-2-line text-[#00d4ff] text-sm" />
            <span className="text-[#00d4ff] text-xs tracking-widest uppercase font-semibold">Support</span>
          </div>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ textShadow: '0 0 30px rgba(0,212,255,0.3)' }}
          >
            Contact Us
          </h1>
          <p className="text-gray-400 text-base tracking-wide max-w-2xl mx-auto">
            We&apos;re here to help. Reach out and we&apos;ll get back to you within 24–48 hours
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-[90%] max-w-[1400px] mx-auto py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Sidebar Navigation */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-1">
              <p className="text-gray-500 text-xs tracking-widest uppercase font-semibold mb-4 px-4">
                Quick Navigation
              </p>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm tracking-wide rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeSection === section.id
                      ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-l-2 border-[#00d4ff]'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-4xl">

            {/* Info Banner */}
            <div className="mb-12 p-5 bg-[#111] border border-[#00d4ff]/10 rounded-xl flex flex-wrap gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <i className="ri-building-line text-[#00d4ff]" />
                <span><span className="text-white font-semibold">Business:</span> Motion Boyz</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-global-line text-[#00d4ff]" />
                <span><span className="text-white font-semibold">Website:</span> motionboyz.org</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-[#00d4ff]" />
                <span><span className="text-white font-semibold">Response:</span> 24–48 hours</span>
              </div>
            </div>

            {/* Get in Touch */}
            <section id="get-in-touch" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Get in Touch</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed mb-8">
                <p>
                  For order inquiries, returns, damaged items, or any other questions — email us directly and our team will get back to you as soon as possible.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Email */}
                <a
                  href="mailto:llc.motionboyz@gmail.com"
                  className="group flex items-start gap-4 p-6 bg-[#111] border border-[#00d4ff]/10 rounded-xl hover:border-[#00d4ff]/40 hover:bg-[#00d4ff]/5 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] flex-shrink-0 group-hover:bg-[#00d4ff]/20 transition-all duration-300">
                    <i className="ri-mail-line text-xl" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm tracking-wide mb-1">Email Us</p>
                    <p className="text-[#00d4ff] text-sm">llc.motionboyz@gmail.com</p>
                    <p className="text-gray-500 text-xs mt-1">Response within 24–48 hours</p>
                  </div>
                </a>

                {/* Brand */}
                <div className="flex items-start gap-4 p-6 bg-[#111] border border-[#00d4ff]/10 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] flex-shrink-0">
                    <i className="ri-store-2-line text-xl" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm tracking-wide mb-1">Brand</p>
                    <p className="text-gray-300 text-sm">Motion Boyz</p>
                    <p className="text-gray-500 text-xs mt-1">Premium Limited Streetwear</p>
                  </div>
                </div>

                {/* Website */}
                <a
                  href="https://motionboyz.org"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="group flex items-start gap-4 p-6 bg-[#111] border border-[#00d4ff]/10 rounded-xl hover:border-[#00d4ff]/40 hover:bg-[#00d4ff]/5 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] flex-shrink-0 group-hover:bg-[#00d4ff]/20 transition-all duration-300">
                    <i className="ri-global-line text-xl" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm tracking-wide mb-1">Website</p>
                    <p className="text-[#00d4ff] text-sm">motionboyz.org</p>
                    <p className="text-gray-500 text-xs mt-1">Official online store</p>
                  </div>
                </a>

                {/* Response Time */}
                <div className="flex items-start gap-4 p-6 bg-[#111] border border-[#00d4ff]/10 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] flex-shrink-0">
                    <i className="ri-time-line text-xl" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm tracking-wide mb-1">Support Hours</p>
                    <p className="text-gray-300 text-sm">Mon – Fri: 9AM – 6PM EST</p>
                    <p className="text-gray-500 text-xs mt-1">Weekends &amp; holidays may vary</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Links */}
            <section id="quick-links" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Quick Links</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed mb-6">
                <p>Looking for a specific policy or information? Find everything you need below.</p>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Return & Refund Policy', path: '/refund', icon: 'ri-refund-2-line', desc: '14-day return window, eligibility & process' },
                  { label: 'Shipping Policy', path: '/shipping', icon: 'ri-truck-line', desc: 'Processing times, delivery estimates & rates' },
                  { label: 'Terms of Service', path: '/terms', icon: 'ri-file-text-line', desc: 'Legal terms governing use of our services' },
                  { label: 'Privacy Policy', path: '/privacy', icon: 'ri-shield-check-line', desc: 'How we collect and protect your data' },
                ].map((link) => (
                  <button
                    key={link.path}
                    onClick={() => window.REACT_APP_NAVIGATE(link.path)}
                    className="w-full flex items-center justify-between p-5 bg-[#111] border border-[#00d4ff]/10 rounded-xl hover:border-[#00d4ff]/30 hover:bg-[#00d4ff]/5 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] flex-shrink-0 group-hover:bg-[#00d4ff]/20 transition-all duration-300">
                        <i className={`${link.icon} text-base`} />
                      </div>
                      <div className="text-left">
                        <p className="text-white text-sm font-semibold group-hover:text-[#00d4ff] transition-colors duration-300">{link.label}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{link.desc}</p>
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-gray-600 group-hover:text-[#00d4ff] transition-colors duration-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </section>

            {/* Support Hours */}
            <section id="support-hours" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#00d4ff] rounded-full" />
                <h2 className="text-3xl font-bold text-white tracking-tight">Support Hours</h2>
              </div>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>Our team is available to assist you during the following hours:</p>
                <div className="bg-[#111] border border-[#00d4ff]/10 rounded-xl overflow-hidden">
                  {[
                    { day: 'Monday', hours: '9:00 AM – 6:00 PM EST' },
                    { day: 'Tuesday', hours: '9:00 AM – 6:00 PM EST' },
                    { day: 'Wednesday', hours: '9:00 AM – 6:00 PM EST' },
                    { day: 'Thursday', hours: '9:00 AM – 6:00 PM EST' },
                    { day: 'Friday', hours: '9:00 AM – 6:00 PM EST' },
                    { day: 'Saturday', hours: 'Limited availability' },
                    { day: 'Sunday', hours: 'Closed' },
                  ].map((item, i) => (
                    <div
                      key={item.day}
                      className={`flex items-center justify-between px-6 py-4 ${i < 6 ? 'border-b border-white/5' : ''}`}
                    >
                      <span className={`text-sm font-medium ${i < 5 ? 'text-white' : 'text-gray-500'}`}>{item.day}</span>
                      <span className={`text-sm ${i < 5 ? 'text-[#00d4ff]' : 'text-gray-500'}`}>{item.hours}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-sm italic">
                  Response times may be longer during holidays, limited drops, or high-volume periods.
                </p>
              </div>
            </section>

            {/* Contact Note */}
            <div className="p-6 border border-[#00d4ff]/20 rounded-xl bg-[#00d4ff]/5 mb-8">
              <p className="text-gray-300 text-sm leading-relaxed">
                For all inquiries — orders, returns, damaged items, or general questions — email us at{' '}
                <a href="mailto:llc.motionboyz@gmail.com" className="text-[#00d4ff] hover:underline cursor-pointer">
                  llc.motionboyz@gmail.com
                </a>
                {' '}and our team will respond as soon as possible.
              </p>
            </div>

            {/* Bottom CTA */}
            <div className="mt-8 p-8 bg-gradient-to-br from-[#00d4ff]/5 to-transparent border border-[#00d4ff]/20 rounded-2xl text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] mx-auto mb-4">
                <i className="ri-shopping-bag-line text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Ready to Shop?</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Explore our exclusive collection of premium limited streetwear designed for those who never stop moving.
              </p>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/shop')}
                className="px-8 py-3 bg-[#00d4ff] text-black font-bold text-sm tracking-widest uppercase rounded-full hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
                style={{ boxShadow: '0 0 30px rgba(0,212,255,0.4)' }}
              >
                Shop Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
